import { useState, useMemo, useCallback } from 'react';
import { CalendarOff } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../common/EmptyState';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import type { LeaveRequestRow } from '../../types';

interface LeaveRequestsTabProps {
  requests: LeaveRequestRow[];
  onRefresh: () => void;
}

type LeaveFilter = 'all' | 'pending' | 'approved' | 'denied';

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};

function leaveStatusStyle(status: string): { bg: string; color: string } {
  if (status === 'approved') return { bg: palette.greenWash, color: palette.green };
  if (status === 'denied') return { bg: palette.redWash, color: palette.red };
  return { bg: palette.amberWash, color: palette.amber };
}

function leaveTypeLabel(type: string): { bg: string; color: string; label: string } {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    vacation: { bg: palette.blueWash, color: palette.blue, label: 'Vacation' },
    sick: { bg: palette.redWash, color: palette.red, label: 'Sick' },
    personal: { bg: palette.amberWash, color: palette.amber, label: 'Personal' },
    other: { bg: palette.g100, color: palette.g500, label: 'Other' },
  };
  return map[type] ?? { bg: palette.g100, color: palette.g500, label: type };
}

function formatDateRange(date: string, endDate: string | null): string {
  const start = new Date(date + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  if (!endDate || endDate === date) return start;
  const end = new Date(endDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  return `${start} – ${end}`;
}

export function LeaveRequestsTab({ requests, onRefresh }: LeaveRequestsTabProps) {
  const [filter, setFilter] = useState<LeaveFilter>('pending');
  const [acting, setActing] = useState<string | null>(null);
  const user = useAuthStore(s => s.user);

  const filtered = useMemo(() => {
    if (filter === 'all') return requests;
    return requests.filter(r => r.status === filter);
  }, [requests, filter]);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const handleAction = useCallback(async (id: string, action: 'approved' | 'denied') => {
    if (!user) return;
    setActing(id);
    try {
      await supabase
        .from('leave_requests')
        .update({ status: action, approved_by: user.id, approved_at: new Date().toISOString() })
        .eq('id', id);
      try { await supabase.from('activity_log').insert({ business_id: user.business_id, activity_type: `leave_${action}`, description: `Leave request ${action}`, entity_type: 'leave_request', entity_id: id, logged_by: user.id }); } catch { /* non-critical */ }
      onRefresh();
    } catch {
      // silently fail — refresh will show current state
    } finally {
      setActing(null);
    }
  }, [user, onRefresh]);

  const filters: { key: LeaveFilter; label: string; count?: number }[] = [
    { key: 'pending', label: 'Pending', count: pendingCount },
    { key: 'approved', label: 'Approved' },
    { key: 'denied', label: 'Denied' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div>
      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 6, padding: 2, marginBottom: 16, width: 'fit-content' }}>
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
            background: filter === f.key ? palette.g800 : 'transparent',
            color: filter === f.key ? palette.white : palette.g500,
            transition: 'all 0.12s',
          }}>
            {f.label}
            {f.count !== undefined && f.count > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '0 5px', borderRadius: 8,
                background: filter === f.key ? palette.white : palette.amber,
                color: filter === f.key ? palette.g800 : palette.white,
              }}>{f.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Team member</th>
              <th style={TH}>Type</th>
              <th style={TH}>Dates</th>
              <th style={TH}>Reason</th>
              <th style={TH}>Status</th>
              <th style={TH}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 0 }}>
                <EmptyState icon={<CalendarOff size={22} />} title={filter === 'pending' ? 'No pending leave requests' : 'No leave requests found'} subtitle="Leave requests from your team will appear here." />
              </td></tr>
            ) : filtered.map(r => {
              const ss = leaveStatusStyle(r.status);
              const tl = leaveTypeLabel(r.type);
              const isActing = acting === r.id;
              return (
                <tr key={r.id}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ ...TD, fontWeight: 500, color: palette.g800 }}>{r.userName}</td>
                  <td style={TD}>
                    <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4, background: tl.bg, color: tl.color }}>{tl.label}</span>
                  </td>
                  <td style={TD}>{formatDateRange(r.date, r.endDate)}</td>
                  <td style={{ ...TD, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', color: palette.g500 }}>{r.reason ?? '—'}</td>
                  <td style={TD}>
                    <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4, background: ss.bg, color: ss.color, textTransform: 'capitalize' }}>{r.status}</span>
                  </td>
                  <td style={TD}>
                    {r.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handleAction(r.id, 'approved')}
                          disabled={isActing}
                          style={{
                            padding: '4px 10px', borderRadius: 5, border: 'none', cursor: isActing ? 'not-allowed' : 'pointer',
                            fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                            background: palette.green, color: palette.white, opacity: isActing ? 0.5 : 1,
                          }}
                        >Approve</button>
                        <button
                          onClick={() => handleAction(r.id, 'denied')}
                          disabled={isActing}
                          style={{
                            padding: '4px 10px', borderRadius: 5, border: `1px solid ${palette.g200}`, cursor: isActing ? 'not-allowed' : 'pointer',
                            fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                            background: palette.white, color: palette.g600, opacity: isActing ? 0.5 : 1,
                          }}
                        >Deny</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: palette.g400 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
